import { Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';

const PricingPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnnual, setIsAnnual] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const plans = [
    {
      name: "Basic",
      description: "Perfect for getting started with your residency search",
      monthlyPrice: 10,
      annualPrice: 8,
      popular: false,
      features: [
        "Basic program matching",
        "Up to 50 program recommendations",
        "USMLE score analysis",
        "Basic filtering options",
        "Email support",
        "Program contact information"
      ],
      color: "from-gray-500 to-gray-600",
      bgColor: "border-gray-200"
    },
    {
      name: "Professional",
      description: "Most popular choice for serious medical students",
      monthlyPrice: 25,
      annualPrice: 20,
      popular: true,
      features: [
        "Advanced AI matching algorithm",
        "Unlimited program recommendations",
        "Detailed probability analysis",
        "Advanced filtering & sorting",
        "Priority email & chat support",
        "Application timeline tracking",
        "Program comparison tools",
        "Interview preparation resources"
      ],
      color: "from-blue-500 to-blue-600",
      bgColor: "border-blue-200 ring-2 ring-blue-100"
    },
    {
      name: "Premium",
      description: "Comprehensive solution with personalized guidance",
      monthlyPrice: 50,
      annualPrice: 40,
      popular: false,
      features: [
        "Everything in Professional",
        "1-on-1 advisor consultations",
        "Custom application strategy",
        "Mock interview sessions",
        "Letter of recommendation guidance",
        "Research opportunity matching",
        "24/7 priority support",
        "Success guarantee program"
      ],
      color: "from-purple-500 to-purple-600",
      bgColor: "border-purple-200"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
        <Link to="/" className="flex items-center">
<img 
  src="/images/logo.png" 
  alt="Residency Companion Logo" 
  className="w-10 h-10 mr-4 object-contain"
/>
        <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
            Residency Companion
        </h1>
        </Link>
            <div className="flex items-center space-x-1">
              <button className="text-gray-600 hover:text-gray-900 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:bg-gray-50">
                Sign In
              </button>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl">
                Get Started
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob-enhanced"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-teal-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob-enhanced animation-delay-2000"></div>
        </div>
        
        <div className={`relative z-10 text-center px-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-thin text-gray-900 mb-6 leading-tight">
              Simple
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent font-light">
                Pricing
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto font-light leading-relaxed">
              Choose the plan that fits your needs. Save thousands on application fees.
            </p>
            {/* Billing Toggle */}
            <div className="flex items-center justify-center space-x-4 mb-12">
              <span className={`text-lg ${!isAnnual ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>Monthly</span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                className={`relative w-14 h-8 rounded-full transition-colors duration-300 ${isAnnual ? 'bg-blue-600' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform duration-300 ${isAnnual ? 'transform translate-x-7' : 'transform translate-x-1'}`}></div>
              </button>
              <span className={`text-lg ${isAnnual ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>
                Annual
                <span className="ml-2 bg-green-100 text-green-600 px-2 py-1 rounded-full text-sm font-medium">Save 20%</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`relative bg-white border-2 ${plan.bgColor} rounded-2xl p-8 transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 ${plan.popular ? 'scale-105' : ''} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-600 to-teal-600 text-white px-6 py-2 rounded-full text-sm font-medium shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-6">{plan.description}</p>
                  
                  <div className="mb-6">
                    <span className="text-5xl font-thin text-gray-900">
                      ${isAnnual ? plan.annualPrice : plan.monthlyPrice}
                    </span>
                    <span className="text-gray-500 ml-2">/ month</span>
                    {isAnnual && (
                      <div className="text-sm text-gray-500 mt-1">
                        Billed annually (${plan.annualPrice * 12})
                      </div>
                    )}
                  </div>

<button className={`w-full bg-gradient-to-r ${plan.color} text-white py-3 px-6 rounded-full font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl`}>
  Buy Now - Final Sale
                  </button>
                </div>

                <div className="space-y-4">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center">
                      <div className={`w-5 h-5 bg-gradient-to-r ${plan.color} rounded-full flex items-center justify-center mr-3 flex-shrink-0`}>
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="text-4xl font-thin text-gray-900 mb-8">
            Why Our Pricing Makes Sense
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <div className="text-3xl mb-4">💸</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Traditional Approach</h3>
              <div className="space-y-2 text-gray-600">
                <div>Application fees: $2,000-5,000</div>
                <div>Interview travel: $3,000-8,000</div>
                <div>Consultation fees: $1,000-3,000</div>
                <div className="border-t pt-2 font-semibold text-red-600">Total: $6,000-16,000</div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-teal-50 p-8 rounded-2xl shadow-lg border border-blue-100">
              <div className="text-3xl mb-4">✨</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">With Residency Companion</h3>
              <div className="space-y-2 text-gray-600">
                <div>Platform fee: $10-50/month</div>
                <div>Targeted applications: Save 60%</div>
                <div>Smart matching: Save 70%</div>
                <div className="border-t pt-2 font-semibold text-green-600">Total Savings: $4,000-12,000</div>
              </div>
            </div>
          </div>

          <p className="text-xl text-gray-600 font-light">
            Invest $10, save thousands. Focus only on programs where you have the best chance of success.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-thin text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
{
  question: "Can I cancel my subscription after purchase?",
  answer: "No, all purchases are final and cannot be cancelled once payment is processed. Due to the immediate access provided to our residency program database, we do not allow cancellations after purchase."
},
{
  question: "Do you offer a free trial?",
  answer: "No, we do not offer free trials. All access requires payment upfront. We recommend reviewing our features and policies carefully before purchasing as all sales are final."
},
{
  question: "What is your refund policy?",
  answer: "We have a strict no-refund policy. All sales are final once you purchase access to our residency program list and premium features. Due to instant digital delivery of proprietary data, absolutely no refunds are available."
},
{
  question: "What happens after I purchase?",
  answer: "After purchase, you gain immediate permanent access to all features in your chosen plan. This access cannot be cancelled or refunded, so please choose carefully and read our policies before buying."
}
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
<section className="py-20 bg-gradient-to-r from-blue-600 to-teal-600">
  <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
    <h2 className="text-4xl md:text-5xl font-thin text-white mb-6">
      Ready to Save Thousands?
    </h2>
    <p className="text-xl text-blue-100 mb-8 font-light">
      Choose your plan and get immediate access. All sales are final.
    </p>
    <div className="space-y-4">
      <button className="bg-white text-blue-600 px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl">
        Choose Your Plan
      </button>
      <div className="text-blue-100 text-sm">
        <Link to="/refund-policy" className="underline hover:text-white">Read our no-refund policy</Link>
        {" "} • {" "}
        <Link to="/cancellation-policy" className="underline hover:text-white">No cancellations after purchase</Link>
      </div>
    </div>
  </div>
</section>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-6">
<img 
  src="/images/logo.png" 
  alt="Residency Companion Logo" 
  className="w-12 h-12 mr-4 object-contain"
/>
                <div>
                  <h4 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                    Residency Companion
                  </h4>
                  <p className="text-sm text-gray-500 font-medium">Medical College Guidance Platform</p>
                </div>
              </div>
              <p className="text-gray-600 font-light">
                Your partner in medical education decisions.
              </p>
            </div>
            <div>
            <h5 className="font-semibold text-gray-900 mb-4">Product</h5>
            <ul className="space-y-3 text-gray-600">
                <li><Link to="/features" className="hover:text-blue-600 transition-colors">Features</Link></li>
                <li><Link to="/how-it-works" className="hover:text-blue-600 transition-colors">How It Works</Link></li>
                <li><Link to="/pricing" className="text-blue-600 font-medium">Pricing</Link></li>
                <li><Link to="/api" className="hover:text-blue-600 transition-colors">API</Link></li>
            </ul>
            </div>
<div>
  <h5 className="font-semibold text-gray-900 mb-4">Support</h5>
  <ul className="space-y-3 text-gray-600">
    <li><Link to="/help" className="hover:text-blue-600 transition-colors">Help Center</Link></li>
    <li><Link to="/contact" className="hover:text-blue-600 transition-colors">Contact Us</Link></li>
    <li><Link to="/refund-policy" className="hover:text-blue-600 transition-colors">Refund Policy</Link></li>
    <li><Link to="/cancellation-policy" className="hover:text-blue-600 transition-colors">Cancellation Policy</Link></li>
    <li><Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy</Link></li>
    <li><Link to="/terms" className="hover:text-blue-600 transition-colors">Terms</Link></li>
  </ul>
</div>
<div>
  <h5 className="font-semibold text-gray-900 mb-4">Company</h5>
  <ul className="space-y-3 text-gray-600">
    <li><Link to="/about" className="hover:text-blue-600 transition-colors">About</Link></li>
  </ul>
</div>
          </div>
          <div className="border-t border-gray-200 mt-12 pt-8 text-center">
            <p className="text-gray-500 text-sm">
              © 2025 Residency Companion. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes blob-enhanced {
          0% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
          25% { transform: translate(20px, -30px) scale(1.05) rotate(5deg); }
          50% { transform: translate(-15px, 10px) scale(0.95) rotate(-3deg); }
          75% { transform: translate(25px, -20px) scale(1.08) rotate(7deg); }
          100% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
        }
        .animate-blob-enhanced { animation: blob-enhanced 9s ease-in-out infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default PricingPage;